import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface MainGroup {
    id: number;
    mainGroupCode: string;
    mainGroupDescription: string;
}

export interface Intrastat {
    id: number;
    code: string;
    internalDescription: string;
}

export interface SubCategory {
    id: number;
    subCategoryCode: string;
    subCategoryName: string;
    mainGroups: MainGroup[];
    intrastats: Intrastat[];
}

export async function getSubCategory(id: number): Promise<SubCategory> {
    const result = await rms2Fetch<SubCategory>(`subcategories/${id}`);
    if (!result) throw new Error('Empty response for getSubCategory');
    return result;
}

export async function saveSubCategory(sc: SubCategory): Promise<SubCategory> {
    const result = await rms2Fetch<SubCategory>('subcategories', {
        method: 'POST',
        body: JSON.stringify({
            id: sc.id,
            subCategoryCode: sc.subCategoryCode,
            subCategoryName: sc.subCategoryName,
            mainGroupIds: sc.mainGroups.map((mg) => mg.id),
            intrastatIds: sc.intrastats.map((i) => i.id)
        })
    });
    if (!result) throw new Error('Empty response for saveSubCategory');
    return result;
}

export async function getMainGroups(): Promise<MainGroup[]> {
    const response = await rms2Fetch<{ mainGroups: MainGroup[] }>('subcategories/maingroups');
    return response?.mainGroups ?? [];
}

export async function getIntrastats(): Promise<Intrastat[]> {
    const response = await rms2Fetch<{ intrastats: Intrastat[] }>('subcategories/intrastats');
    return response?.intrastats ?? [];
}
